import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import apiService from '../services/api';
import '../styles/bloomberg-theme.css';

interface EquityChartProps {
  period?: string;
  userId?: string;
  showTWR?: boolean;
  isConsolidated?: boolean;
}

const EquityChartBloomberg: React.FC<EquityChartProps> = ({ period = '7d', userId, showTWR = false, isConsolidated = false }) => {
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchChartData = useCallback(async () => {
    try {
      setLoading(true);
      const data = isConsolidated
        ? await apiService.getConsolidatedChart(period)
        : userId
        ? await apiService.getAdminUserChart(userId, period)
        : await apiService.getChartData(period);

      const formattedData = data.timestamps.map((timestamp: string, index: number) => ({
        timestamp,
        time: new Date(timestamp).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }),
        date: new Date(timestamp).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        }),
        value: data.values[index]
      }));

      setChartData(formattedData);
      setError(null);
    } catch (err) {
      setError('Failed to load chart data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [period, userId, showTWR, isConsolidated]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'var(--bb-bg-secondary)',
          border: '1px solid var(--bb-amber)',
          padding: '8px',
          fontFamily: 'var(--bb-font-mono)',
          fontSize: '11px'
        }}>
          <div style={{ color: 'var(--bb-amber)', marginBottom: '4px' }}>{label}</div>
          <div style={{ color: 'var(--bb-text-data)' }}>
            VALUE: {formatCurrency(payload[0].value)}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'var(--bb-amber)' }}>LOADING CHART DATA...</span>
      </div>
    );
  }

  if (error || chartData.length === 0) {
    return (
      <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'var(--bb-red)' }}>{error || 'NO DATA AVAILABLE'}</span>
      </div>
    );
  }

  const minValue = Math.min(...chartData.map(d => d.value));
  const maxValue = Math.max(...chartData.map(d => d.value));
  const padding = (maxValue - minValue) * 0.1;

  return (
    <div style={{ width: '100%', height: '400px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 10, bottom: 30 }}
        >
          <defs>
            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#00FF00" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#00FF00" stopOpacity={0.1}/>
            </linearGradient>
          </defs>

          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--bb-dark-gray)"
            vertical={false}
          />

          <XAxis
            dataKey="date"
            stroke="var(--bb-gray)"
            tick={{ fill: 'var(--bb-gray)', fontSize: 10 }}
            axisLine={{ stroke: 'var(--bb-gray)' }}
          />

          <YAxis
            stroke="var(--bb-gray)"
            tick={{ fill: 'var(--bb-gray)', fontSize: 10 }}
            axisLine={{ stroke: 'var(--bb-gray)' }}
            domain={[minValue - padding, maxValue + padding]}
            tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`}
          />

          <Tooltip content={<CustomTooltip />} />

          <Area
            type="monotone"
            dataKey="value"
            stroke="#00FF00"
            strokeWidth={2}
            fill="url(#colorValue)"
            dot={false}
            activeDot={{ r: 4, fill: 'var(--bb-amber)' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default EquityChartBloomberg;